// MST
import { types, flow, cast } from 'mobx-state-tree'

import { 
  ICertificationSchemaGetAllAttrs,
  ICertificationSchemaGetAttrs,
  ICertificationSchemaSaveAttrs,
} from 'interfaces/back-office/content-management/certification/schema/certification-schema-view.interface'
import {
  ICertificationSchemaApiGetAllResponse,
  ICertificationSchemaApiGetResponse,
  ICertificationSchemaApiSaveResponse,
} from 'interfaces/back-office/content-management/certification/schema/certification-schema-api.interface'
import { ICertificationSchemaDetail, ICertificationSchemaList } from 'interfaces/back-office/content-management/certification/schema'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Lodash
import omit from 'lodash/omit'

// Certification Schema Props
const certificationSchemaProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Schema List Props
const certificationSchemaFieldProps = {
  fieldId: types.maybeNull(types.number),
  fieldTitle: types.maybeNull(types.string),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

const certificationProcedureListProps = {
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string)
}

const certificationSchemaDetailCompetencyDetailProps = {
  content: types.maybeNull(types.string)
}

const certificationSchemaDetailRegisterSchemaProps = {
  content: types.maybeNull(types.string)
}

// Certification Schema Detail Props
const certificationSchemaDetailProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  competencyDetail: types.maybeNull(types.array(types.model('Certification Competenct Detail').props(certificationSchemaDetailCompetencyDetailProps))),
  registerSchema: types.maybeNull(types.array(types.model('Certification Register Schema').props(certificationSchemaDetailRegisterSchemaProps))),
  minimumPoints: types.maybeNull(types.number),
  certificationPrecondition: types.maybeNull(types.array(types.model('Certification Precondition').props(certificationProcedureListProps))),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Schema List Model
const CertificationSchemaList = types.array(
  types.model('CertificationSchemaList').props({
    ...certificationSchemaFieldProps,
    certificationSchemas: types.maybeNull(types.array(types.model('Certification Schema').props(certificationSchemaProps)))
  })
)

// Certification Schema Detail Model
const CertificationSchemaDetail = types
  .model('CertificationSchemaDetail').props(omit(certificationSchemaDetailProps, ['number']))

const CertificationSchemaStoreModel = types
  .model('CertificationSchema')
  .props({
    certificationSchema_isLoading: types.optional(types.boolean, false),
    certificationSchema_isSaveLoading: types.optional(types.boolean, false),
    certificationSchema_certificationSchemaList: types.optional(CertificationSchemaList, []),
    certificationSchema_detail: types.optional(CertificationSchemaDetail, {}),
  })
  .actions(self => ({
    certificationSchema_SET_LOADING: (payload: boolean): void => {
      self.certificationSchema_isLoading = payload
    },
    certificationSchema_SET_SAVE_LOADING: (payload: boolean): void => {
      self.certificationSchema_isSaveLoading = payload
    },
    certificationSchema_SET_CERTIFICATION_SCHEMA_LIST: (payload: ICertificationSchemaList[]): void => {
      self.certificationSchema_certificationSchemaList = cast(payload)
    },
    certificationSchema_SET_CERTIFICATION_SCHEMA: (payload: ICertificationSchemaDetail): void => {
      self.certificationSchema_detail = cast(JSON.parse(JSON.stringify(payload)))
    },
    certificationSchema_CLEAR_CERTIFICATION_SCHEMA_LIST: (): void => {
      self.certificationSchema_certificationSchemaList = cast([])
    },
    certificationSchema_CLEAR_CERTIFICATION_SCHEMA: (): void => {
      self.certificationSchema_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch certificationSchemas
     *
     * @param {ICertificationSchemaGetAllAttrs} payload
     *
     * @return {any} any
     */
    certificationSchema_fetchCertificationSchemaList: flow(function* (payload: ICertificationSchemaGetAllAttrs) {
      self.certificationSchema_SET_LOADING(true)

      const response: ICertificationSchemaApiGetAllResponse = yield self.environment.certificationSchemaApi.certificationSchema_getCertificationSchemaList({
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.certificationSchema_SET_CERTIFICATION_SCHEMA_LIST(response.res.data)

      self.certificationSchema_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification schema
     *
     * @param {ICertificationSchemaGetAttrs} payload
     *
     * @return {any} any
     */
    certificationSchema_fetchCertificationSchema: flow(function* (payload: ICertificationSchemaGetAttrs) {
      self.certificationSchema_SET_LOADING(true)

      const response: ICertificationSchemaApiGetResponse = yield self.environment.certificationSchemaApi.certificationSchema_getCertificationSchema({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.certificationSchema_SET_CERTIFICATION_SCHEMA(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.certificationSchema_SET_LOADING(false)
    }),

    /**
     * @description Save certification schema
     *
     * @param {ICertificationSchemaSaveAttrs} payload
     *
     * @return {any} any
     */
    certificationSchema_saveCertificationSchema: flow(function* (payload: ICertificationSchemaSaveAttrs) {
      self.certificationSchema_SET_SAVE_LOADING(true)

      const response: ICertificationSchemaApiSaveResponse = yield self.environment.certificationSchemaApi.certificationSchema_saveCertificationSchema({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.certificationSchema_SET_SAVE_LOADING(false)
    }),
  }))

  export const createCertificationSchemaStoreDefaultModel = () => types.optional(CertificationSchemaStoreModel, {})